import { Zap, TrendingUp } from "lucide-react";
import { useRealtimeCredits } from "@/hooks/useRealtimeCredits";
import { useAppSettings } from "@/hooks/useAppSettings";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface CreditsBadgeProps {
  variant?: "compact" | "full";
  className?: string;
}

export function CreditsBadge({ variant = "compact", className }: CreditsBadgeProps) {
  const { usage, limits, isLoading, formatTokens, getAlertLevel } = useRealtimeCredits();
  const { data: appSettings } = useAppSettings();

  // Se esconder tokens estiver ativado, não renderizar
  if (appSettings?.hideTokensDisplay) {
    return null;
  }

  if (isLoading || !usage || !limits) {
    return (
      <div className={cn("animate-pulse", className)}>
        <div className="h-8 w-32 bg-muted rounded-md" />
      </div>
    );
  }

  const dailyAlertLevel = getAlertLevel(usage.dailyPercentage);
  const monthlyAlertLevel = getAlertLevel(usage.monthlyPercentage);
  
  // Usar o nível de alerta mais crítico
  const alertLevel = usage.dailyPercentage >= usage.monthlyPercentage 
    ? dailyAlertLevel 
    : monthlyAlertLevel;

  const badgeColors = {
    safe: "bg-green-100 text-green-800 border-green-300 hover:bg-green-200",
    warning: "bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200",
    danger: "bg-red-100 text-red-800 border-red-300 hover:bg-red-200",
  };

  const iconColors = {
    safe: "text-green-600",
    warning: "text-yellow-600",
    danger: "text-red-600",
  };

  if (variant === "compact") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className={cn(
                "cursor-help gap-1.5 transition-all",
                badgeColors[alertLevel],
                className
              )}
            >
              <Zap className={cn("w-3.5 h-3.5 drop-shadow-sm", iconColors[alertLevel])} />
              <span className="font-semibold">
                {formatTokens(usage.dailyRemaining)}
              </span>
              <span className="text-xs opacity-75">/ dia</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="w-72 p-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b">
                <TrendingUp className="w-4 h-4 text-primary" />
                <h4 className="font-semibold text-sm">Créditos de IA</h4>
              </div>
              
              {/* Daily Usage */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Uso Diário</span>
                  <span className="font-medium">
                    {formatTokens(usage.dailyUsed)} / {formatTokens(limits.dailyLimit)}
                  </span>
                </div>
                <Progress 
                  value={usage.dailyPercentage} 
                  className={cn(
                    "h-2",
                    "[&>div]:transition-all",
                    dailyAlertLevel === 'danger' && "[&>div]:bg-red-500",
                    dailyAlertLevel === 'warning' && "[&>div]:bg-yellow-500",
                    dailyAlertLevel === 'safe' && "[&>div]:bg-green-500"
                  )}
                />
                <div className="text-xs text-muted-foreground">
                  {formatTokens(usage.dailyRemaining)} tokens restantes hoje
                </div>
              </div>

              {/* Monthly Usage */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Uso Mensal</span>
                  <span className="font-medium">
                    {formatTokens(usage.monthlyUsed)} / {formatTokens(limits.monthlyLimit)}
                  </span>
                </div>
                <Progress 
                  value={usage.monthlyPercentage} 
                  className={cn(
                    "h-2",
                    "[&>div]:transition-all",
                    monthlyAlertLevel === 'danger' && "[&>div]:bg-red-500",
                    monthlyAlertLevel === 'warning' && "[&>div]:bg-yellow-500",
                    monthlyAlertLevel === 'safe' && "[&>div]:bg-green-500"
                  )}
                />
                <div className="text-xs text-muted-foreground">
                  {formatTokens(usage.monthlyRemaining)} tokens restantes este mês
                </div>
              </div>

              {(usage.isNearDailyLimit || usage.isNearMonthlyLimit) && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-yellow-600 dark:text-yellow-500 font-medium">
                    ⚠️ Você está próximo do limite. Considere fazer upgrade.
                  </p>
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Full variant - mais detalhado
  return (
    <div className={cn("rounded-lg border bg-card p-4 space-y-4", className)}>
      <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className={cn("w-5 h-5 drop-shadow-sm", iconColors[alertLevel])} />
            <h3 className="font-semibold text-sm">Créditos de IA</h3>
          </div>
        <Badge variant="outline" className={badgeColors[alertLevel]}>
          {alertLevel === 'safe' && '✓ Normal'}
          {alertLevel === 'warning' && '⚠ Atenção'}
          {alertLevel === 'danger' && '⛔ Crítico'}
        </Badge>
      </div>

      {/* Daily Usage */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground font-medium">Hoje</span>
          <span className="font-semibold">
            {formatTokens(usage.dailyUsed)} / {formatTokens(limits.dailyLimit)}
          </span>
        </div>
        <Progress 
          value={usage.dailyPercentage} 
          className={cn(
            "h-2.5",
            "[&>div]:transition-all",
            dailyAlertLevel === 'danger' && "[&>div]:bg-red-500",
            dailyAlertLevel === 'warning' && "[&>div]:bg-yellow-500",
            dailyAlertLevel === 'safe' && "[&>div]:bg-green-500"
          )}
        />
        <div className="text-xs text-muted-foreground">
          {formatTokens(usage.dailyRemaining)} tokens restantes • Reseta em 24h
        </div>
      </div>

      {/* Monthly Usage */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground font-medium">Este Mês</span>
          <span className="font-semibold">
            {formatTokens(usage.monthlyUsed)} / {formatTokens(limits.monthlyLimit)}
          </span>
        </div>
        <Progress 
          value={usage.monthlyPercentage} 
          className={cn(
            "h-2.5",
            "[&>div]:transition-all",
            monthlyAlertLevel === 'danger' && "[&>div]:bg-red-500",
            monthlyAlertLevel === 'warning' && "[&>div]:bg-yellow-500",
            monthlyAlertLevel === 'safe' && "[&>div]:bg-green-500"
          )}
        />
        <div className="text-xs text-muted-foreground">
          {formatTokens(usage.monthlyRemaining)} tokens restantes este mês
        </div>
      </div>
    </div>
  );
}
